import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { apiFetch } from '../utils/apiFetch';
import { GENERAL_SETTINGS_KEY, getTenantStorageKey } from '../utils/tenantSettingsStorage';

const empty = { key: '', recipientName: '', city: '' };
const names = { cardapio: 'Brutos Cardápio', zap: 'Brutos Zap' };
const TenantCompanyPixPanel = ({ apiUrl, session, canEdit }) => {
  const [draft, setDraft] = useState(empty);
  const [revision, setRevision] = useState(null);
  const [sync, setSync] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [status, setStatus] = useState({ loading: true, saving: false, error: '', saved: false });
  const mounted = useRef(false);
  const readGeneration = useRef(0);
  const syncReadSequence = useRef(0);
  const isCurrent = () => mounted.current && Boolean(getTenantStorageKey(GENERAL_SETTINGS_KEY, session));
  const headers = { Authorization: `Bearer ${session.token}`, 'Content-Type': 'application/json' };
  const read = async () => {
    const response = await apiFetch(`${apiUrl}/api/settings/company/pix`, { headers });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Não foi possível carregar o Pix.');
    if (String(data.companyId) !== String(session.user.companyId) || !Number.isSafeInteger(data.revision)) throw new Error('A configuração Pix não pertence a esta sessão.');
    return data;
  };
  const apply = (data) => {
    setDraft(data.pix); setRevision(data.revision); setDefaults(data.defaults || {});
    setSync(data.sync || []); setConsumers(data.consumers || []);
  };
  useEffect(() => {
    mounted.current = true;
    const generation = ++readGeneration.current;
    read().then((data) => {
      if (!isCurrent() || generation !== readGeneration.current) return;
      apply(data); setStatus({ loading: false, saving: false, error: '', saved: false });
    }).catch((error) => {
      if (isCurrent() && generation === readGeneration.current) setStatus({ loading: false, saving: false, error: error.message, saved: false });
    });
    const interval = setInterval(() => {
      if (!isCurrent()) return;
      const generation = readGeneration.current;
      const sequence = ++syncReadSequence.current;
      read().then((data) => {
        if (isCurrent() && generation === readGeneration.current && sequence === syncReadSequence.current) { setSync(data.sync || []); setConsumers(data.consumers || []); }
      }).catch(() => {});
    }, 5000);
    return () => { mounted.current = false; readGeneration.current += 1; clearInterval(interval); };
    // This component remounts for every authenticated tenant/token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const change = (field) => (event) => {
    const value = event.target.value;
    setDraft((before) => ({ ...before, [field]: value,
      ...(field === 'key' && value && !before.key ? { recipientName: before.recipientName || defaults.recipientName || '', city: before.city || defaults.city || '' } : {}),
      ...(field === 'key' && !value.trim() ? { recipientName: '', city: '' } : {}),
    }));
    setStatus((before) => ({ ...before, saved: false }));
  };
  const reload = async () => {
    readGeneration.current += 1;
    setStatus((before) => ({ ...before, loading: true, error: '', saved: false }));
    try {
      const data = await read(); if (!isCurrent()) return;
      apply(data); setStatus({ loading: false, saving: false, error: '', saved: false });
    } catch (error) { if (isCurrent()) setStatus((before) => ({ ...before, loading: false, error: error.message })); }
  };
  const save = async () => {
    if (!canEdit || revision === null || !isCurrent() || status.saving) return;
    readGeneration.current += 1;
    setStatus((before) => ({ ...before, saving: true, error: '', saved: false }));
    try {
      const response = await apiFetch(`${apiUrl}/api/settings/company/pix`, { method: 'PUT', headers,
        body: JSON.stringify({ pix: draft, expectedRevision: revision }) });
      const data = await response.json(); if (!isCurrent()) return;
      if (!response.ok) throw new Error(data.error || 'Não foi possível salvar o Pix.');
      const authoritative = await read(); if (!isCurrent()) return;
      apply(authoritative); setStatus({ loading: false, saving: false, error: '', saved: true });
    } catch (error) { if (isCurrent()) setStatus((before) => ({ ...before, saving: false, error: error.message, saved: false })); }
  };
  const disabled = !canEdit || status.loading || status.saving || revision === null;
  return <Card className="configuracoes-card" sx={{ mt: 3 }}><CardContent>
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Pagamento Pix</Typography>
        {(status.loading || status.saving) && <CircularProgress size={20} />}
      </Stack>
      <Typography color="text.secondary">Cadastre a chave que receberá os pagamentos dos clientes no Brutos Zap e no Brutos Cardápio.</Typography>
      {status.error && <Alert severity="error">{status.error}</Alert>}
      {status.saved && <Alert severity="success">Chave Pix salva na loja.</Alert>}
      <TextField label="Chave Pix" value={draft.key} onChange={change('key')} disabled={disabled} fullWidth
        helperText="CPF, CNPJ, e-mail, telefone com +55 ou chave aleatória. Deixe vazio para remover."
        inputProps={{ maxLength: 100 }} />
      <TextField label="Nome do recebedor Pix" value={draft.recipientName} onChange={change('recipientName')} disabled={disabled || !draft.key} fullWidth inputProps={{ maxLength: 120 }} />
      <TextField label="Cidade do recebedor Pix" value={draft.city} onChange={change('city')} disabled={disabled || !draft.key} fullWidth inputProps={{ maxLength: 100 }} />
      {revision > 0 && consumers.map((consumer) => {
        const state = sync.find((item) => item.consumer === consumer);
        const synced = state?.status === 'synced' && state.revision === revision;
        return <Typography key={consumer} variant="body2" color={synced ? 'success.main' : 'text.secondary'}>
          {names[consumer]}: {synced ? 'sincronizado' : 'sincronização pendente; nova tentativa automática'}
        </Typography>;
      })}
      <Typography variant="body2" color="text.secondary">O QR Code facilita o pagamento. A loja precisa conferir o recebimento antes de confirmar a quitação.</Typography>
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={reload} disabled={status.loading || status.saving}>Recarregar Pix</Button>
        <Button variant="contained" onClick={save} disabled={disabled}>{status.saving ? 'Salvando...' : 'Salvar Pix'}</Button>
      </Stack>
    </Stack>
  </CardContent></Card>;
};
export default function CompanyPixPanel(props) {
  const { session } = props;
  if (!session?.token || !session?.user?.companyId) return null;
  return <TenantCompanyPixPanel key={`${session.user.companyId}:${session.token}`} {...props} />;
}
